import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Card } from "@shadcn-in-nx/ui/card";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import TicketDetailNav from "./TicketDetailNav";
import TicketDetailHeader from "./TicketDetailHeader";
import TicketMessageList from "./TicketMessageList";
import TicketReplyBox from "./TicketReplyBox";
import TicketResourceLibrary from "./TicketResourceLibrary";
import TicketDetailSidebar from "./TicketDetailSidebar";
import useTicketQuickAction from "./useTicketQuickAction";
import useTicketDetailKeyboard from "./useTicketDetailKeyboard";
import { useManageCompany } from "../ManageCompany/ManageCompanyContext";

export default function AdminTicketDetail({ ticketId, onBack, onNavigate, ticketIds = [], availableTags = [] }) {
    const { t } = useTranslation();
    const { open: openManageCompany } = useManageCompany();
    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const { call: fetchAdminTicket, isLoading } = useBluelagoon({ method: "getAdminTicket" });
    const { call: updateAdminTicket, isLoading: isUpdating } = useBluelagoon({ method: "updateAdminTicket" });

    const currentIndex = ticketIds.indexOf(ticketId);
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex >= 0 && currentIndex < ticketIds.length - 1;

    const fetchTicket = useCallback(async () => {
        try {
            const response = await fetchAdminTicket({ id: ticketId });
            if (response?.data?.ticket) {
                setTicket(response.data.ticket);
                setMessages(response.data.ticket.messages || []);
            }
        } catch {
            toast.error(t("admin.support.fetchError", "Failed to load ticket"));
        }
    }, [ticketId, fetchAdminTicket, t]);

    useEffect(() => {
        fetchTicket();
        try {
            const views = JSON.parse(localStorage.getItem("support_ticket_views") || "{}");
            views[ticketId] = new Date().toISOString();
            localStorage.setItem("support_ticket_views", JSON.stringify(views));
            localStorage.setItem("support_last_viewed_ticket", ticketId);
        } catch {}
    }, [fetchTicket, ticketId]);

    const handleUpdate = useCallback(async (updates) => {
        try {
            const response = await updateAdminTicket({ id: ticketId, ...updates });
            if (response?.status === 200) {
                setTicket((prev) => ({ ...prev, ...updates }));
                return true;
            }
        } catch {}
        toast.error(t("admin.support.updateError", "Failed to update ticket"));
        return false;
    }, [ticketId, updateAdminTicket, t]);

    const { quickActionUndoMode, handleQuickAction } = useTicketQuickAction({ ticket, ticketId, handleUpdate, setTicket, t });

    const replyBoxRef = useRef(null);
    const goPrev = useCallback(() => { if (hasPrev) onNavigate(ticketIds[currentIndex - 1]); }, [hasPrev, onNavigate, ticketIds, currentIndex]);
    const goNext = useCallback(() => { if (hasNext) onNavigate(ticketIds[currentIndex + 1]); }, [hasNext, onNavigate, ticketIds, currentIndex]);
    useTicketDetailKeyboard({
        enabled: !!ticket,
        onResolve: () => handleQuickAction("resolve"),
        onClose: () => handleQuickAction("close"),
        onNext: goNext,
        onPrev: goPrev,
        onFocusReply: () => replyBoxRef.current?.focusEditor(),
        onAIGenerate: () => replyBoxRef.current?.triggerAIGenerate(),
    });

    const handleToggleArchive = useCallback(async () => {
        const archive = !ticket?.archivedAt;
        const ok = await handleUpdate({ archived: archive });
        if (ok) {
            setTicket((prev) => ({ ...prev, archivedAt: archive ? new Date().toISOString() : null }));
            toast.success(archive ? t("admin.support.ticketArchived", "Ticket archived") : t("admin.support.ticketUnarchived", "Ticket unarchived"));
        }
    }, [ticket?.archivedAt, handleUpdate, t]);

    if (isLoading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

    if (!ticket) return (
        <div className="p-4 sm:p-6">
            <TicketDetailNav ticket={null} onBack={onBack} hasPrev={false} hasNext={false} />
            <Card className="p-8 sm:p-12 text-center mt-4"><p className="text-muted-foreground">{t("admin.support.ticketNotFound", "Ticket not found")}</p></Card>
        </div>
    );

    return (
        <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
            <TicketDetailNav ticket={ticket} onBack={onBack}
                hasPrev={hasPrev} hasNext={hasNext}
                onPrev={goPrev}
                onNext={goNext}
                onToggleArchive={handleToggleArchive} isUpdating={isUpdating}
                onRefresh={fetchTicket} isRefreshing={isLoading}
                onQuickAction={handleQuickAction} quickActionUndoMode={quickActionUndoMode}
                onManageCompany={ticket?.company?.id ? () => openManageCompany(ticket.company.id) : undefined} />
            <TicketDetailHeader ticket={ticket} availableTags={availableTags} isUpdating={isUpdating}
                onUpdateStatus={async (s) => { const ok = await handleUpdate({ status: s }); if (ok) toast.success("Status updated"); }}
                onUpdatePriority={async (p) => { const ok = await handleUpdate({ priority: p }); if (ok) toast.success("Priority updated"); }}
                onUpdateTags={(tags) => handleUpdate({ tags })} />
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                <div className="flex-1 min-w-0 space-y-3 sm:space-y-4 order-2 lg:order-1">
                    <TicketMessageList messages={messages} />
                    {ticket.status !== "CLOSED" && (
                        <>
                            <TicketReplyBox ref={replyBoxRef} key={ticketId} ticketId={ticketId} onMessageSent={(msg) => setMessages((prev) => [...prev, msg])} />
                            <TicketResourceLibrary
                                customerLocale={ticket?.createdBy?.language || "en"}
                                onInsert={(title, url) => replyBoxRef.current?.insertResource(title, url)} />
                        </>
                    )}
                </div>
                <div className="order-1 lg:order-2">
                    <TicketDetailSidebar ticket={ticket} />
                </div>
            </div>
        </div>
    );
}
